import { io } from "socket.io-client";
import { URL_SOCKET } from "Constants";

// Single socket for the whole app. It does not connect by itself: the server
// requires the same JWT as the REST API, so Entry connects it once the user is
// authenticated and disconnects it on logout. The token is read on every
// (re)connection so it is never stale.
export const getSocket = () => {
  if (!window.socket) {
    window.socket = io(URL_SOCKET, {
      transports: ["websocket"],
      autoConnect: false,
      auth: (cb) => cb({ token: window.localStorage.getItem("token") }),
    });
    window.socket.on("connect_error", (error) => {
      if (error && error.message === UNAUTHORIZED) handleUnauthorized();
    });
    window.socket.on("connect", resetReconnectBackoff);
    window.socket.on("disconnect", (reason) => {
      if (reason === SERVER_DISCONNECT) scheduleReconnect();
    });
  }
  return window.socket;
};

const UNAUTHORIZED = "unauthorized";

// The server drops a user's sockets when their access changes (cargo assigned
// or reverted, categories edited, account deactivated). socket.io-client does
// NOT auto-reconnect after that reason, so we reconnect ourselves with the
// fresh token. Every other reason (network loss, ping timeout, transport
// error) is handled by the library. The server re-joins the user's own room
// and INTERNAL on the handshake; rooms joined with joinRoom are re-joined on
// every "connect". If the reconnect is rejected as "unauthorized" (user
// deactivated), the connect_error handler above logs out.
const SERVER_DISCONNECT = "io server disconnect";
const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30000;
let reconnectAttempts = 0;
let reconnectTimer = null;

function resetReconnectBackoff() {
  reconnectAttempts = 0;
}

function cancelReconnect() {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
}

// Exponential backoff (1s, 2s, 4s ... 30s) that only resets on a successful
// connect, so a server that keeps dropping us never causes a tight loop.
function scheduleReconnect() {
  cancelReconnect();
  const delay = Math.min(RECONNECT_BASE_MS * 2 ** reconnectAttempts, RECONNECT_MAX_MS);
  reconnectAttempts += 1;
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    const socket = getSocket();
    if (!socket.connected) socket.connect();
  }, delay);
}

// The server rejected the session token (expired, user deactivated): same
// logout as Navbar / Policy (drop the token) and reload to the login screen.
// Network errors have other messages and never end the session.
const handleUnauthorized = () => {
  window.localStorage.removeItem("token");
  window.location.reload();
};

// Joins a room now and again on every reconnect (the server forgets rooms when
// a socket reconnects). Returns the cleanup that leaves it.
export const joinRoom = (room) => {
  const socket = getSocket();
  const join = () => socket.emit("join", room);
  join();
  socket.on("connect", join);
  return () => {
    socket.off("connect", join);
    socket.emit("leave", room);
  };
};

export const syncSocketWithAuth = (authed) => {
  const socket = getSocket();
  if (authed) {
    if (!socket.connected) socket.connect();
  } else {
    cancelReconnect();
    socket.disconnect();
  }
  return socket;
};
